import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { UserProfile } from '@bcc/shared';
import { apiFetch } from '../lib/api';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../constants/siteUi';
import { useAuth } from '../contexts/AuthContext';

export function StaffDashboard() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [pending, setPending] = useState<UserProfile[]>([]);
  const [noteUser, setNoteUser] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const res = await apiFetch<{ users: UserProfile[] }>('/api/staff/pending-investors', { token });
      setPending(res.users);
    })();
  }, [token]);

  async function approve(id: string, approved: boolean) {
    if (!token) return;
    await apiFetch(`/api/users/${id}/approval`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ approved }),
    });
    setPending((rows) => rows.filter((u) => u.id !== id));
  }

  async function submitNote(e: FormEvent) {
    e.preventDefault();
    if (!token || !noteUser) return;
    await apiFetch('/api/notes', {
      method: 'POST',
      token,
      body: JSON.stringify({ user_id: noteUser, body: noteBody }),
    });
    setNoteBody('');
    setStatus('Note saved');
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 space-y-10">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">Staff operations</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--home-muted-on-dark)]">
          Review investor onboarding flags, drop CRM notes, and coordinate with compliance before publishing new deal
          collateral.
        </p>
      </div>
      <section className={SITE_GLASS_CARD}>
        <header className="border-b border-white/[0.08] px-6 py-4 text-sm font-semibold text-[var(--home-text-on-dark)]">
          Pending investor approvals
        </header>
        <table className="w-full text-sm">
          <thead className="border-b border-white/[0.06] bg-white/[0.04] text-left text-xs uppercase text-[var(--home-muted-on-dark)]">
            <tr>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Accreditation</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {pending.map((u) => (
              <tr key={u.id} className="border-t border-white/[0.06]">
                <td className="px-6 py-3 text-[var(--home-text-on-dark)]">{u.email}</td>
                <td className="px-6 py-3 text-[var(--home-text-on-dark)]">{u.full_name}</td>
                <td className="px-6 py-3 capitalize text-[var(--home-text-on-dark)]">{u.accreditation_status.replace('_', ' ')}</td>
                <td className="px-6 py-3 text-right space-x-2">
                  <button
                    type="button"
                    className="text-xs px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500"
                    onClick={() => void approve(u.id, true)}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="rounded-full bg-white/[0.08] px-3 py-1 text-xs text-[var(--home-text-on-dark)] ring-1 ring-white/15 transition hover:bg-white/[0.12]"
                    onClick={() => void approve(u.id, false)}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
            {pending.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-[var(--home-muted-on-dark)]">
                  No pending applications.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
      <section className={`max-w-xl space-y-4 p-6 ${SITE_GLASS_CARD}`}>
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">CRM note</h2>
        <form className="space-y-3" onSubmit={submitNote}>
          <input
            required
            placeholder="Investor user UUID"
            value={noteUser}
            onChange={(e) => setNoteUser(e.target.value)}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
          <textarea
            required
            rows={4}
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            className={`w-full ${SITE_FORM_CONTROL}`}
          />
          <button
            type="submit"
            className="rounded-full bg-[var(--home-miami-blue)] px-5 py-2 text-sm font-semibold text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105"
          >
            Save note
          </button>
          {status ? <p className="text-xs text-emerald-300">{status}</p> : null}
        </form>
      </section>
    </div>
  );
}
